import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  classifySubscriptionPromoTerms,
  formatPromoCurrency,
  hasSubscriptionPromoRenewalTerms,
  MAX_PROMO_CODE_LENGTH,
  SUBSCRIPTION_PROMO_TERM_VARIANTS,
} from "../../utils/subscriptionPromo";

const SubscriptionPromoCode = ({
  promo,
  idPrefix = "subscription",
  getQuoteContext,
  buttonClassName = "purple-button",
  collapsible = false,
  currentPriceAlreadyProminent = false,
  disabled = false,
}) => {
  const { i18n, t } = useTranslation();
  const feedbackRef = useRef(null);
  const [expanded, setExpanded] = useState(
    !collapsible || Boolean(promo.draftCode || promo.quote)
  );
  const inputId = `${idPrefix}-promo-code`;
  const feedbackId = `${inputId}-feedback`;
  const summaryId = `${inputId}-summary`;
  const bodyId = `${inputId}-body`;
  const quote = promo.quote;
  const termsVariant = classifySubscriptionPromoTerms(quote);
  const hasRenewalTerms = hasSubscriptionPromoRenewalTerms(quote);
  const checkoutInstructionLabelKey =
    termsVariant === SUBSCRIPTION_PROMO_TERM_VARIANTS.FREE_VERIFICATION
      ? "subscription.promo.checkoutInstructionVerificationLabel"
      : "subscription.promo.checkoutInstructionLabel";
  const hasFeedbackMessage =
    promo.status === "checking" || promo.status === "error";

  const initialTermsKey = {
    [SUBSCRIPTION_PROMO_TERM_VARIANTS.AFFILIATE_MONTHLY]:
      "subscription.promo.affiliateNow",
    [SUBSCRIPTION_PROMO_TERM_VARIANTS.FREE_VERIFICATION]:
      "subscription.promo.freeVerification",
    [SUBSCRIPTION_PROMO_TERM_VARIANTS.PARTIAL_REGULAR]:
      "subscription.promo.partialNow",
    [SUBSCRIPTION_PROMO_TERM_VARIANTS.LEGACY]:
      "subscription.promo.legacyTerms",
  }[termsVariant];
  const initialTerms = quote
    ? t(initialTermsKey, {
        discountPercent: quote.discountPercent,
        count: quote.durationDays,
      })
    : null;
  const currentAmount = quote
    ? formatPromoCurrency(
        quote.initialAmount,
        quote.currency,
        i18n?.language
      )
    : null;
  const renewalTermsKey = hasRenewalTerms
    ? quote.autoRenew === true
      ? "subscription.promo.autoRenews"
      : quote.autoRenew === false
        ? "subscription.promo.doesNotAutoRenew"
        : "subscription.promo.currentRegular"
    : null;
  const renewalTerms =
    quote && renewalTermsKey
      ? t(renewalTermsKey, {
          amount: formatPromoCurrency(
            quote.renewalAmount,
            quote.currency,
            i18n?.language
          ),
          count: quote.renewalDurationDays,
        })
      : null;

  useEffect(() => {
    if (promo.status === "error") {
      setExpanded(true);
      feedbackRef.current?.focus();
    }
  }, [promo.status, promo.errorKey]);

  const apply = () => {
    if (disabled) {
      return;
    }

    return promo.apply(getQuoteContext?.() || {});
  };

  const handleKeyDown = (event) => {
    if (event.key !== "Enter") {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    apply();
  };

  return (
    <div
      role="group"
      className={`subscription-promo${
        collapsible ? " subscription-promo--collapsible" : ""
      }${expanded ? " is-expanded" : ""}`}
      aria-labelledby={
        collapsible ? `${inputId}-toggle` : `${inputId}-label`
      }
    >
      {collapsible && (
        <button
          type="button"
          id={`${inputId}-toggle`}
          className="subscription-promo-toggle"
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={() => setExpanded((current) => !current)}
        >
          <span>{t("subscription.promo.toggle")}</span>
          <span className="subscription-promo-toggle-icon" aria-hidden="true">
            +
          </span>
        </button>
      )}

      <div id={bodyId} className="subscription-promo-body" hidden={!expanded}>
        <label id={`${inputId}-label`} htmlFor={inputId}>
          {t("subscription.promo.label")}
        </label>
        <div className="subscription-promo-controls">
          <input
            id={inputId}
            name="promoCode"
            type="text"
            value={promo.draftCode}
            onChange={(event) => {
              if (!disabled) {
                promo.changeDraftCode(event.target.value);
              }
            }}
            onKeyDown={handleKeyDown}
            placeholder={t("subscription.promo.placeholder")}
            maxLength={MAX_PROMO_CODE_LENGTH}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            translate="no"
            disabled={disabled || promo.isChecking}
            aria-invalid={promo.status === "error"}
            aria-describedby={feedbackId}
          />
          <button
            type="button"
            className={`${buttonClassName} subscription-promo-apply`}
            disabled={
              disabled || promo.isChecking || !promo.draftCode.trim()
            }
            onClick={apply}
          >
            {promo.isChecking
              ? t("subscription.promo.checking")
              : t("subscription.promo.apply")}
          </button>
        </div>

        <p
          id={feedbackId}
          ref={feedbackRef}
          className={`subscription-promo-feedback ${promo.status}`}
          role={
            promo.status === "error"
              ? "alert"
              : hasFeedbackMessage
                ? "status"
                : undefined
          }
          aria-live={
            hasFeedbackMessage
              ? promo.status === "error"
                ? "assertive"
                : "polite"
              : undefined
          }
          aria-atomic={hasFeedbackMessage ? "true" : undefined}
          tabIndex={promo.status === "error" ? -1 : undefined}
        >
          {promo.status === "checking"
            ? t("subscription.promo.checking")
            : null}
          {promo.status === "error" && promo.errorKey
            ? t(promo.errorKey)
            : null}
        </p>

        {quote && (
          <div
            id={summaryId}
            className="subscription-promo-summary"
            role={hasFeedbackMessage ? undefined : "status"}
            aria-live={hasFeedbackMessage ? undefined : "polite"}
            aria-atomic={hasFeedbackMessage ? undefined : "true"}
          >
            {quote.provisional && (
              <p className="subscription-promo-summary-provisional">
                {t("subscription.promo.provisional")}
              </p>
            )}
            <div className="subscription-promo-summary-sections">
              <div
                className="subscription-promo-summary-section subscription-promo-summary-code"
                role="group"
                aria-labelledby={`${summaryId}-code-label`}
              >
                <p
                  id={`${summaryId}-code-label`}
                  className="subscription-promo-summary-label"
                >
                  {t("subscription.promo.recognizedCodeLabel")}
                </p>
                <p className="subscription-promo-summary-code-value">
                  <code>{quote.code}</code>
                </p>
              </div>
              <div
                className="subscription-promo-summary-section subscription-promo-summary-access-section"
                role="group"
                aria-labelledby={`${summaryId}-access-label`}
              >
                <p
                  id={`${summaryId}-access-label`}
                  className="subscription-promo-summary-label"
                >
                  {t("subscription.promo.accessLabel")}
                </p>
                <p className="subscription-promo-summary-access">
                  {initialTerms}
                </p>
              </div>
              {!currentPriceAlreadyProminent && (
                <div
                  className="subscription-promo-summary-section subscription-promo-summary-current-price"
                  role="group"
                  aria-labelledby={`${summaryId}-current-price-label`}
                >
                  <p
                    id={`${summaryId}-current-price-label`}
                    className="subscription-promo-summary-label"
                  >
                    {t("subscription.promo.currentPriceLabel")}
                  </p>
                  <p className="subscription-promo-summary-current-amount">
                    {currentAmount}
                  </p>
                </div>
              )}
              {currentPriceAlreadyProminent && (
                <p className="subscription-promo-summary-current-price-accessible visually-hidden">
                  {t("subscription.promo.currentPriceLabel")}: {currentAmount}
                </p>
              )}
              {renewalTerms && (
                <div
                  className="subscription-promo-summary-section subscription-promo-summary-renewal-section"
                  role="group"
                  aria-labelledby={`${summaryId}-renewal-label`}
                >
                  <p
                    id={`${summaryId}-renewal-label`}
                    className="subscription-promo-summary-label"
                  >
                    {t("subscription.promo.renewalLabel")}
                  </p>
                  <p className="subscription-promo-summary-renewal">
                    {renewalTerms}
                  </p>
                </div>
              )}
            </div>
            {quote.checkoutInstruction && (
              <div
                className="subscription-promo-instruction"
                role="note"
                aria-label={t(checkoutInstructionLabelKey)}
              >
                <p className="subscription-promo-instruction-label">
                  {t(checkoutInstructionLabelKey)}
                </p>
                <p className="subscription-promo-instruction-text">
                  {quote.checkoutInstruction}
                </p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPromoCode;
